import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import moment from 'moment/moment'

import { SupplierPortalService } from '../../../core/services/supplier-portal/supplier-portal.service'
import { VehicleModelService } from '../../../core/services/vehicle/vehicle-model/vehicle-model.service'
import { VehicleBrandService } from '../../../core/services/vehicle/vehicle-brand/vehicle-brand.service'
import { VehicleTypeService } from '../../../core/services/vehicle/vehicle-type/vehicle-type.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorVeiculoEdit, FornecedorVeiculoVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { SupplierPortalDocumentsComponent } from '../supplier-portal-documents/supplier-portal-documents.component'

// Edição de veículo já cadastrado — sem placa, identidade não muda.
// Uso principal: renovar validade do seguro, e gerenciar os documentos anexados
// (o id já existe nesse fluxo, diferente do cadastro).
@Component({
  selector: 'modal-vehicle-edit',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormField, MatInput, MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinner,
    SupplierPortalDocumentsComponent,
  ],
  templateUrl: './modal-vehicle-edit.component.html',
  styleUrl: './modal-vehicle-edit.component.scss',
})
export class ModalVehicleEditComponent implements OnInit {
  @Output() response = new EventEmitter<boolean>()

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { veiculo: FornecedorVeiculoVm },
    private dialogRef: MatDialogRef<ModalVehicleEditComponent>,
    private supplierPortalService: SupplierPortalService,
    private vehicleModelService: VehicleModelService,
    private vehicleBrandService: VehicleBrandService,
    private vehicleTypeService: VehicleTypeService,
    private snackbar: SnackbarCustomService,
  ) {}

  vehicleModels: any[] = []
  vehicleBrands: any[] = []
  vehicleTypes: any[] = []
  // Filtro de UI: modelo só é selecionável depois de escolher a marca (fluxo
  // recomendado pela API — não é enviado no payload, só reduz a lista do select).
  marcaControl = new FormControl<number | null>(null)
  form!: FormGroup
  saving = false
  // FornecedorVeiculoVm só traz a descrição do modelo (não o id) — o select fica sem
  // valor pré-selecionado até o usuário escolher de novo, e avisamos na tela.
  modeloNaoIdentificado = false

  get filteredVehicleModels(): any[] {
    const marcaId = this.marcaControl.value
    if (!marcaId) return []
    return this.vehicleModels.filter((item: any) => item.veiculoMarcaId === marcaId)
  }

  private readonly currentYear = new Date().getFullYear()

  ngOnInit(): void {
    this.createForm()
    this.loadVehicleModels()
    this.loadVehicleBrands()
    this.loadVehicleTypes()
    this.marcaControl.valueChanges.subscribe(() => {
      this.form.get('veiculoModeloId')?.setValue(null)
    })
  }

  private createForm(): void {
    const v = this.data.veiculo
    this.form = this.fb.group({
      veiculoModeloId: [null, Validators.required],
      tipoVeiculoId: [v.tipoVeiculoId ?? null, Validators.required],
      cor: [v.cor || '', Validators.required],
      ano: [v.ano ?? null, [Validators.min(1950), Validators.max(this.currentYear + 1)]],
      crlv: [v.crlv || ''],
      seguro: [v.seguro || ''],
      seguroValidade: [this.parseDate(v.seguroValidade)],
    })
  }

  private parseDate(value?: string | null): Date | null {
    if (!value) return null
    const parsed = moment(value, 'DD/MM/YYYY')
    return parsed.isValid() ? parsed.toDate() : null
  }

  private loadVehicleModels(): void {
    this.vehicleModelService.listVehicleModel().subscribe({
      next: (res: any) => {
        this.vehicleModels = res.data || []
        this.preselectModelo()
      },
    })
  }

  private loadVehicleBrands(): void {
    this.vehicleBrandService.listVehicleBrand().subscribe({
      next: (res: any) => { this.vehicleBrands = res.data || [] },
    })
  }

  private loadVehicleTypes(): void {
    this.vehicleTypeService.listVehicleType().subscribe({
      next: (res: any) => { this.vehicleTypes = res.data || [] },
    })
  }

  // Tenta casar a descrição vinda da listagem restrita (só texto, sem id) com a lista
  // completa de modelos. Se não achar, deixa em branco e avisa — o usuário reseleciona.
  // Marca é setada primeiro (sem disparar o reset do modelo) pra depois o modelo
  // já entrar preenchido dentro da lista filtrada por ela.
  private preselectModelo(): void {
    const descricaoAtual = (this.data.veiculo.modelo || '').trim()
    const match = this.vehicleModels.find((item: any) => {
      const label = `${item.tipoVeiculoDescricao ?? ''} - ${item.modelo ?? ''}`.trim()
      return item.modelo === descricaoAtual || label === descricaoAtual
    })
    if (match) {
      this.marcaControl.setValue(match.veiculoMarcaId, { emitEvent: false })
      this.form.get('veiculoModeloId')?.setValue(match.id)
    } else {
      this.modeloNaoIdentificado = true
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    const v = this.form.value
    const model: FornecedorVeiculoEdit = {
      veiculoModeloId: v.veiculoModeloId,
      tipoVeiculoId: v.tipoVeiculoId,
      cor: v.cor,
      ano: v.ano ?? null,
      crlv: v.crlv || '',
      seguro: v.seguro || '',
      seguroValidade: v.seguroValidade ? moment(v.seguroValidade).format('DD/MM/YYYY') : '',
    }
    this.supplierPortalService.editVeiculo(this.data.veiculo.id, model).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message || 'Veículo atualizado com sucesso.')
        this.response.emit(true)
        this.dialogRef.close()
      },
      error: (err: ErrorRequestModel) => { this.saving = false; this.snackbar.errorRequest(err) },
      complete: () => { this.saving = false },
    })
  }

  close(): void { this.dialogRef.close() }
}
